import { shippingGroup_shippingGroup_items_attribute_options } from 'src/queries/__generated__/shippingGroup';
import { format, addDays } from 'date-fns';
import { monthNames, daysNames, nth } from 'src/util/formatDate';

type ExtractDaysOptions = {
    preferLeadingDaysBefore24?: boolean;
};

export const extractDaysFromShippingGroupName = (
    shippingGroupName: string,
    noDropshipping: boolean,
    options: ExtractDaysOptions = {},
): number | null => {
    if (!shippingGroupName) return null;

    const normalizedName = shippingGroupName.replace(/\s+/g, '').toLowerCase();

    if (options.preferLeadingDaysBefore24) {
        const leadingDaysBefore24Pattern = /^(\d+).*24$/i;
        const leadingDaysBefore24Match = normalizedName.match(leadingDaysBefore24Pattern);
        if (leadingDaysBefore24Match) {
            return parseInt(leadingDaysBefore24Match[1], 10);
        }
    }

    if (noDropshipping) {
        const dropshippingPattern = /^(\d+)days?.*24$/i;
        const dropshippingMatch = normalizedName.match(dropshippingPattern);
        if (dropshippingMatch) {
            return parseInt(dropshippingMatch[1], 10);
        }
    }

    const oneDayPatterns = [/nextday/i, /24hour/i, /bundle.*24/i, /.*24$/i];
    for (const pattern of oneDayPatterns) {
        if (pattern.test(normalizedName)) {
            return 1;
        }
    }

    const generalDayPattern = /(\d+)\s*days?/i;
    const generalMatch = shippingGroupName.match(generalDayPattern);
    if (generalMatch) {
        return parseInt(generalMatch[1], 10);
    }

    return null;
};

export const getLatestDeliveryShippingGroup = (
    shippingGroupNames: string[],
    noDropshipping: boolean,
    options: ExtractDaysOptions = {},
): string | null => {
    const validNames = shippingGroupNames.filter((name) => name);

    if (validNames.length === 0) return null;

    return validNames.reduce((latest, current) => {
        const latestDays = extractDaysFromShippingGroupName(latest, noDropshipping, options) ?? 0;
        const currentDays = extractDaysFromShippingGroupName(current, noDropshipping, options) ?? 0;

        return currentDays > latestDays ? current : latest;
    });
};

type HolidaysListProps = {
    date: string;
    end: Date;
    name: string;
    rule: string;
    start: Date;
    type: string;
}[];

// Helper to get current time in UK timezone
const getNowInLondon = (): Date => {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
    const parts = formatter.formatToParts(now);
    const get = (type: string) => parts.find((p) => p.type === type)?.value || '0';
    return new Date(
        Number(get('year')),
        Number(get('month')) - 1,
        Number(get('day')),
        Number(get('hour')),
        Number(get('minute')),
        Number(get('second')),
    );
};

export const timeLeft = (tomorrowHours: number, tomorrowMinutes = 0) => {
    // Convert current time to UK timezone (Europe/London) for comparison with cutoff time
    const todayInLondon = getNowInLondon();
    const tomorrowInLondon = new Date(addDays(todayInLondon, 1).setHours(tomorrowHours, tomorrowMinutes, 0));
    const deadline = new Date(tomorrowInLondon.getTime() - todayInLondon.getTime());

    return format(deadline, 'H') + 'hrs ' + format(deadline, 'm') + ' mins';
};

const calculateDays = (
    date: string | Date,
    numberOfDaysToAdd: number,
    holidayList: HolidaysListProps,
    hourLimit: number,
    minuteLimit: number,
    skipCutoffCheck = false,
): Date => {
    const calculateDaysToAddAndWeekends = (
        startDate: Date,
        _endDate: Date,
        holidayList: HolidaysListProps,
        numberOfDaysToAdd: number,
    ) => {
        let workDaysCount = 0;
        const currentDate = new Date(startDate);

        while (workDaysCount < numberOfDaysToAdd) {
            const isWeekend = currentDate.getDay() === 0 || currentDate.getDay() === 6;
            const isHoliday = holidayList.some((h) => h.start.toDateString() === currentDate.toDateString());
            if (!isWeekend && !isHoliday) {
                workDaysCount++;
            }
            currentDate.setDate(currentDate.getDate() + 1);
        }
        currentDate.setHours(1, 0, 0, 0);
        return currentDate;
    };

    // Convert current time to UK timezone (Europe/London) for comparison with cutoff time
    const nowInLondon = getNowInLondon();

    // Use UK date for cutoff comparison - cutoff time comes from UK Magento
    const limitTime = new Date(nowInLondon).setHours(hourLimit, minuteLimit, 0);

    const effectiveStart = new Date(date);
    if (!skipCutoffCheck && nowInLondon > new Date(limitTime)) {
        effectiveStart.setDate(effectiveStart.getDate() + 1);
    }

    const finalDate = calculateDaysToAddAndWeekends(
        effectiveStart,
        new Date(new Date(effectiveStart).setDate(new Date(effectiveStart).getDate() + numberOfDaysToAdd)),
        holidayList,
        numberOfDaysToAdd,
    );

    const finalDateBasedOnHour = new Date(finalDate.setDate(finalDate.getDate()));

    // Keep skipping weekends and holidays until we find a valid business day
    const isHolidayDate = (d: Date) => holidayList.some((holiday) => holiday.start.toDateString() === d.toDateString());
    const isWeekendDate = (d: Date) => d.getDay() === 0 || d.getDay() === 6;

    while (isHolidayDate(finalDateBasedOnHour) || isWeekendDate(finalDateBasedOnHour)) {
        finalDateBasedOnHour.setDate(finalDateBasedOnHour.getDate() + 1);
    }

    return finalDateBasedOnHour;
};

const formatDate = (date: Date) => {
    const getDay = date.getDate();
    const getMonth = date.getMonth();
    const getYear = date.getFullYear();

    const nextDate = new Date(`${getDay} ${monthNames[getMonth]} ${getYear}`);

    const dayName = daysNames[nextDate.getDay()];
    const monthName = monthNames[nextDate.getMonth()];

    return `${dayName} ${getDay}${nth(getDay)} ${monthName}`;
};

export const timePlusDays = (
    numberOfDays: number,
    holidaysList: HolidaysListProps,
    hourLimit: number,
    _date: Date,
    minuteLimit = 0,
) => {
    let deliveryDay: Date;

    // Use UK date for all calculations since cutoff time and warehouse are in UK
    const todayInLondon = getNowInLondon();
    // Reset time to start of day for date comparisons
    const todayDateInLondon = new Date(todayInLondon.getFullYear(), todayInLondon.getMonth(), todayInLondon.getDate());

    const isTodayHoliday = holidaysList.filter(
        (el) => el.start.toDateString() === todayDateInLondon.toDateString() && el,
    );

    if (isTodayHoliday.length !== 0) {
        deliveryDay = calculateDays(isTodayHoliday[0].end, numberOfDays, holidaysList, hourLimit, minuteLimit, true);
        return { formatedDate: formatDate(deliveryDay), date: deliveryDay };
    }

    deliveryDay = calculateDays(todayDateInLondon, numberOfDays, holidaysList, hourLimit, minuteLimit);

    return { formatedDate: formatDate(deliveryDay), date: deliveryDay };
};

export const getShippingInfo = (
    shippingGroupList: shippingGroup_shippingGroup_items_attribute_options[],
    types: string[],
) => {
    const nonExpressTypeInfo = shippingGroupList?.filter((type) => type?.value === types[0]);

    if (nonExpressTypeInfo && nonExpressTypeInfo.length > 0) return nonExpressTypeInfo[0]?.label;

    return '2DaysPlain24';
};

const NEXT_DAY_DELIVERY_BANNER_GROUPS = ['Next Day Delivery', '2DaysPlain24', '2DayPlain24', '3DaysPlain24'];

export const shouldShowNextDayDeliveryMessage = (
    shippingTypesArray: string[],
    shippingGroupList: shippingGroup_shippingGroup_items_attribute_options[],
): boolean => {
    if (!shippingTypesArray.length) return false;

    const uniqueShippingCodes = Array.from(new Set(shippingTypesArray));

    return uniqueShippingCodes.every((code) => {
        const label = shippingGroupList.find((group) => group.value === code)?.label;
        return Boolean(label && NEXT_DAY_DELIVERY_BANNER_GROUPS.includes(label));
    });
};

export const getDeliveryMethods = (
    shippingTypesArray: string[],
    shippingGroupList: shippingGroup_shippingGroup_items_attribute_options[],
) => {
    const expressShippingLabels = [
        '2DaysPlain',
        '2DayPlain24',
        '2DaysPlain24',
        '3DaysPlain24',
        'Next Day Delivery',
        'Next Day Delivery Hi Vis',
        'Bundles 24 Hour Dispatch',
    ];
    const sameDayDeliveryCode = shippingGroupList.find((type) => type.label === 'Same Day Dispatch Customised')?.value;

    const expressShippingCode = shippingGroupList.find(
        (type) => expressShippingLabels.includes(type.label) && shippingTypesArray.includes(type.value),
    )?.value;

    const showExpress = expressShippingCode && shippingTypesArray.includes(expressShippingCode);

    return {
        showExpress,
        sameDayDelivery: sameDayDeliveryCode && shippingTypesArray.includes(sameDayDeliveryCode),
    };
};

export const extractDaysFromDeliveryMethod = (
    shippingGroupList: shippingGroup_shippingGroup_items_attribute_options[],
    shippingTypesArray: string[],
    noDropshipping: boolean,
    options: ExtractDaysOptions = {},
) => {
    const availableShippingGroups = shippingGroupList
        .filter((listGroup) => shippingTypesArray.includes(listGroup.value))
        .map((filteredGroup) => filteredGroup.label);

    const latestGroup = getLatestDeliveryShippingGroup(availableShippingGroups, noDropshipping, options);

    if (latestGroup) {
        return extractDaysFromShippingGroupName(latestGroup, noDropshipping, options) ?? 2;
    }

    return 2;
};

export const calculateBusinessDaysBetween = (
    startDate: Date,
    endDate: Date,
    holidayList: HolidaysListProps,
    cutOffTime: string[],
): number => {
    let businessDays = 0;
    const currentDate = new Date(startDate);

    currentDate.setHours(0, 0, 0, 0);
    currentDate.setDate(currentDate.getDate() + 1);

    if (cutOffTime && cutOffTime.length > 0) {
        let cutOffHour: number;
        let cutOffMinute = 0;

        if (cutOffTime[0].includes(':')) {
            const [hour, minute] = cutOffTime[0].split(':');
            cutOffHour = parseInt(hour, 10);
            cutOffMinute = parseInt(minute, 10) || 0;
        } else {
            cutOffHour = parseInt(cutOffTime[0], 10);
            cutOffMinute = cutOffTime[1] ? parseInt(cutOffTime[1], 10) : 0;
        }

        // Convert current time to UK timezone (Europe/London) for comparison
        const nowInLondon = getNowInLondon();
        const cutOffToday = new Date(nowInLondon);
        cutOffToday.setHours(cutOffHour, cutOffMinute, 0, 0);

        if (nowInLondon > cutOffToday) {
            currentDate.setDate(currentDate.getDate() + 1);
        }
    }

    const targetDate = new Date(endDate);
    targetDate.setHours(0, 0, 0, 0);

    while (currentDate <= targetDate) {
        const isWeekend = currentDate.getDay() === 0 || currentDate.getDay() === 6;
        const isHoliday = holidayList.some((h) => h.start.toDateString() === currentDate.toDateString());

        if (!isWeekend && !isHoliday) {
            businessDays++;
        }
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return businessDays;
};
